import type { RegisteredComponent } from "@builder.io/sdk-qwik";
import { register } from "@builder.io/sdk-qwik";

import { FeatureSection } from "./ui/feature-section";
import { Heading } from "./ui/heading";
import { Prose } from "./ui/prose";
import { Button } from "./ui/button";
import { Centered } from "./helpers/centered";
import { Link } from "@builder.io/qwik-city";
import { PricingCard } from "./ui/pricing-card";
import { MembershipPlan } from "~/lib/schemas";
import { BuilderCard } from "./helpers/builder-card";
import { Container } from "./helpers/container";
import { Columns } from "./helpers/columns";
import { CTASection } from "./ui/cta-section";
import { FeatureList } from "./feature-list";
import { AuroraBackground } from "./ui/aurora-background";
import { MembershipPlanDetails } from "./membership-plan-details";
import { SavingsComparisonTable } from "./savings-comparison-table";
import { ImageWithCaption } from "./ui/image-with-caption";
import { SessionLifetimeDealModal } from "./session-lifetime-deal-modal";
import { Testimonials } from "./ui/testimonials";
import { baseSize, borderRadius, boxShadow } from "@unocss/preset-mini/theme";
import { CcoCarousel } from "./cco-carousel";
import { CCOList } from "./cco-list";

export const configureBuilder = () => {
  register("editor.settings", {
    hideHeatMap: true,
    hideFormComponents: true,
    hideDataTab: true,
    hideABTab: true,
    hidePageUrlEditor: false,
    hideTemplates: true,

    styleStrictMode: true,
    designTokens: {
      colors: ["brand", "violet", "orange", "gray"].flatMap((color) =>
        [
          "50",
          "100",
          "200",
          "300",
          "400",
          "500",
          "600",
          "700",
          "800",
          "900",
          "950",
        ].map((brightness) => ({
          name: `${color}-${brightness}`,
          value: `var(--${color}-${brightness})`,
        })),
      ),
      spacing: [
        ...[2, 4, 8, 16, 24, 32, 48, 64, 96, 128].map((size) => ({
          name: `${size}`,
          value: `calc(var(--spacing) * ${size})`,
        })),
        ...Object.entries(baseSize).map(([name, value]) => ({
          name,
          value: value,
        })),
      ],
      fontFamily: [
        { name: "Heading", value: "var(--font-heading, sans-serif)" },
        { name: "Primary", value: "var(--font-sans, sans-serif)" },
      ],
      borderRadius: Object.entries(borderRadius).map(([name, value]) => ({
        name,
        value: value,
      })),
      boxShadow: Object.entries(boxShadow).map(([name, value]) => ({
        name,
        value: (Array.isArray(value) ? value : [value]).join(", "),
      })),
    },
  });
};

/**
 * This array is used to integrate custom components within Builder.
 * https://www.builder.io/c/docs/custom-components-intro
 *
 * These components will be found the "Custom Components"
 * section of Builder's visual editor.
 * You can also turn on "components only mode" to limit
 * editing to only these components.
 * https://www.builder.io/c/docs/guides/components-only-mode
 */
export const CUSTOM_COMPONENTS: RegisteredComponent[] = [
  {
    component: Container,
    name: "Container",
    canHaveChildren: true,
    inputs: [
      {
        name: "pagePadding",
        type: "boolean",
        required: false,
      },
      {
        name: "maxWidth",
        type: "string",
        enum: ["prose", "sm", "md", "lg", "xl", "2xl", "full"],
        required: false,
      },
    ],
  },
  {
    component: Columns,
    name: "Distributed Columns",
    canHaveChildren: true,
    inputs: [
      {
        name: "count",
        type: "string",
        enum: ["1", "2", "3", "4"],
        required: false,
      },
    ],
  },
  {
    component: FeatureList,
    name: "FeatureList",
    friendlyName: "Feature List",
    inputs: [
      {
        name: "features",
        type: "list",
        subFields: [{ name: "feature", type: "string", required: true }],
        required: true,
      },
    ],
  },
  {
    component: PricingCard,
    name: "PricingCard",
    canHaveChildren: true,
    inputs: [
      {
        name: "membershipPlan",
        type: "string",
        enum: Object.values(MembershipPlan),
        required: false,
      },
      { name: "price", type: "string", required: false },
      { name: "priceSuffix", type: "string", required: false },
      { name: "description", type: "string", required: false },
      { name: "highlighted", type: "boolean", defaultValue: false },
    ],
  },
  {
    component: Prose,
    name: "Prose",
    inputs: [
      {
        name: "content",
        type: "richText",
        defaultValue: "<p>This is some content</p>",
        required: true,
      },
    ],
  },
  {
    component: BuilderCard,
    name: "Card",
    canHaveChildren: true,
    inputs: [
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "fillHeight",
        type: "boolean",
        required: false,
      },
    ],
  },
  {
    component: CTASection,
    name: "CTA Section",
    inputs: [
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "link",
        type: "string",
        required: false,
      },
      {
        name: "linkText",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: Link,
    name: "Link",
    canHaveChildren: true,
    inputs: [
      {
        name: "href",
        type: "string",
        required: true,
      },
      {
        name: "target",
        type: "string",
        enum: ["_self", "_blank", "_parent", "_top"],
      },
    ],
    childRequirements: {
      message: "You can only put Text or a DCMButton inside a Link",
      query: {
        "component.name": { $in: ["Text", "DCMButton"] },
      },
    },
  },
  {
    component: Button,
    name: "DCMButton",
    canHaveChildren: true,
    childRequirements: {
      message: "You can only put Text inside a Button",
      query: {
        "component.name": { $in: ["Text"] },
      },
    },
    defaultChildren: [
      {
        "@type": "@builder.io/sdk:Element",
        component: {
          name: "Text",
          options: { text: "I am child text block!" },
        },
      },
    ],
    inputs: [
      {
        name: "variant",
        type: "string",
        defaultValue: "default",
        enum: ["default", "secondary", "fancy", "outline", "ghost"],
        required: true,
      },
      {
        name: "size",
        type: "string",
        defaultValue: "default",
        enum: ["default", "sm", "lg"],
        required: true,
      },
    ],
  },
  {
    component: FeatureSection,
    name: "FeatureSection",
    inputs: [
      {
        name: "layout",
        type: "string",
        enum: ["left", "right"],
        required: true,
      },
      {
        name: "look",
        type: "string",
        enum: ["active", "inactive"],
        required: true,
      },
      {
        name: "preHeadline",
        type: "string",
        required: true,
      },
      {
        name: "headline",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
    ],
  },
  {
    component: MembershipPlanDetails,
    name: "MembershipPlanDetails",
    friendlyName: "Plan Details Table (expandable)",
  },
  {
    component: Heading,
    name: "Heading",
    inputs: [
      {
        name: "class",
        type: "string",
      },
      {
        name: "preHeadline",
        type: "string",
      },
      {
        name: "headline",
        type: "string",
      },
      {
        name: "subheadline",
        type: "string",
      },
      {
        name: "isH1",
        type: "boolean",
        friendlyName: "Top-level heading? (H1)",
        defaultValue: false,
      },
    ],
  },
  {
    component: Centered,
    name: "Centered",
    canHaveChildren: true,
  },
  {
    component: AuroraBackground,
    name: "AuroraBackground",
    friendlyName: "Aurora Background",
    canHaveChildren: true,
  },
  {
    component: SavingsComparisonTable,
    name: "SavingsComparisonTable",
    description:
      "A table comparing software costs and savings across different membership plans",
    inputs: [
      {
        name: "class",
        type: "string",
      },
    ],
  },
  {
    component: ImageWithCaption,
    name: "ImageWithCaption",
    friendlyName: "Image with Caption",
    canHaveChildren: true,
    inputs: [
      {
        name: "caption",
        type: "string",
        required: true,
      },
    ],
  },
  {
    component: SessionLifetimeDealModal,
    name: "Session Lifetime Deal Modal",
    friendlyName: "Session Lifetime Deal Modal",
  },
  {
    component: Testimonials,
    name: "Testimonials",
    friendlyName: "Testimonials",
    inputs: [
      {
        name: "testimonials",
        type: "list",
        subFields: [
          {
            name: "author",
            type: "string",
            required: true,
          },
          {
            name: "role",
            type: "string",
            required: false,
          },
          {
            name: "title",
            type: "string",
            required: false,
          },
          {
            name: "content",
            type: "richText",
            required: true,
          },
          {
            name: "setHTML",
            type: "boolean",
            defaultValue: true,
            hideFromUI: false,
          },
        ],
      },
    ],
  },
  {
    component: CcoCarousel,
    name: "CcoCarousel",
    friendlyName: "CCO Carousel",
    canHaveChildren: false,
  },
  {
    component: CCOList,
    name: "CCOList",
    friendlyName: "CCO List",
    canHaveChildren: false,
  },
];
